import React from 'react';
import { PosEventoInfo } from '../../interfaces/Car';
import '../../shared/ui/badge-status.css';
import './selo-pos-evento.css';

interface SeloPosEventoProps {
  /** O objeto da resposta do veículo. Nulo/ausente ⇒ nada é renderizado. */
  posEvento?: PosEventoInfo | null;
  /**
   * at-134: substitui o texto do hover. Existe para a tela do setor de Eventos, que recebe só o
   * FATO (ADR 0064) — sem data de entrega da origem, e o texto padrão diria "data de entrega não
   * registrada", que é afirmação falsa sobre o atendimento de origem.
   */
  titulo?: string;
}

/**
 * Selo "Pós-evento" (at-96), ao lado da placa nos modais e nas listagens.
 *
 * Por que existe: um retorno de garantia indistinguível de serviço novo faz a oficina tratar
 * retrabalho como produção. O molde é o selo de motivo da pendência (at-89), e o CSS é o
 * compartilhado (`shared/ui/badge-status.css`) — nunca classe de status própria por tela.
 *
 * ⚠️ **Célula de tabela é UMA linha** (regra do Victor, 12/08/2026): o selo vai AO LADO da
 * placa, nunca empilhado.
 *
 * ⚠️ O texto do `title` é o detalhe completo — a mesma informação que o modal mostra por
 * extenso. Sem data de entrega registrada (a origem pode ter chegado a ENTREGA pelo
 * `updateStatus` manual), a frase omite a data em vez de dizer "há 0 dias".
 *
 * 🔴 "Pós-evento" é o vocabulário INTERNO. O associado lê "retorno de garantia", e esse rótulo
 * vem do backend (PDF e tela pública) — não recriar a tradução aqui.
 *
 * 🔴 at-106: o selo DISTINGUE as duas origens, e isso não é detalhe estético. "Pós-evento" sozinho
 * diz ao Chefe de Oficina "já fizemos este serviço" — falso quando o serviço original não está no
 * sistema, e ele vai procurar um histórico que não existe. Daí o segundo rótulo.
 */
const SeloPosEvento: React.FC<SeloPosEventoProps> = ({ posEvento, titulo }) => {
  if (!posEvento) return null;

  return (
    <span className="cilia-badge badge-laranja selo-pos-evento" title={titulo ?? detalheDoSelo(posEvento)}>
      {posEvento.origemNoSistema ? 'Pós-evento' : 'Pós-evento externo'}
    </span>
  );
};

/** O texto do hover: o que este atendimento está retomando. */
export function detalheDoSelo(posEvento: PosEventoInfo): string {
  // 🔴 at-106: sem atendimento de origem, o texto para no que se sabe — o serviço não está no
  // sistema. Dizer "foi feito em oficina externa" seria afirmar uma causa que ninguém mediu:
  // pode ser oficina externa, serviço anterior ao go-live, ou cadastro que ninguém fez.
  if (!posEvento.origemNoSistema) {
    const evento =
      posEvento.origemEventNumber !== null ? ` (evento ${posEvento.origemEventNumber})` : '';
    return `Retorno de garantia de um serviço que não está no sistema${evento}`;
  }

  if (!posEvento.origemEntregueEm) {
    return 'Retorno de garantia de um serviço anterior deste veículo (data de entrega não registrada)';
  }

  const data = new Date(posEvento.origemEntregueEm).toLocaleDateString('pt-BR');
  const dias = posEvento.diasDesdeEntrega;
  const sufixo = dias === null ? '' : ` (há ${dias} ${dias === 1 ? 'dia' : 'dias'})`;

  return `Retorno de garantia do serviço entregue em ${data}${sufixo}`;
}

/**
 * at-134 (ADR 0064): o selo para quem recebe só o FATO — a tela do setor de Eventos, cuja projeção
 * traz `{ origem }` e nada do atendimento de origem (nem evento, nem data de entrega). O texto do
 * hover para no que essa projeção sabe.
 */
export const SeloPosEventoPorOrigem: React.FC<{ origem?: 'INTERNO' | 'EXTERNO' | null }> = ({ origem }) => {
  if (!origem) return null;
  const posEvento: PosEventoInfo = {
    origemCarId: null,
    origemNoSistema: origem === 'INTERNO',
    origemEventNumber: null,
    origemEntregueEm: null,
    diasDesdeEntrega: null,
  } as PosEventoInfo;
  return (
    <SeloPosEvento
      posEvento={posEvento}
      titulo={
        origem === 'EXTERNO'
          ? 'Retorno de garantia de um serviço que não está no sistema'
          : 'Retorno de garantia de um serviço feito nesta oficina'
      }
    />
  );
};

export default SeloPosEvento;
